/**
 * Route Royal Page Header Component
 * 
 * Clean, professional header for static pages
 */

'use client';

import type { PageHeaderProps } from '@routeroyal/theme-core';
import Image from 'next/image';
import Link from 'next/link';

/**
 * RouteRoyalPageHeader - Route Royal-themed header section for static pages
 * 
 * Displays the page title, subtitle, excerpt, and optional featured image
 * with Route Royal blue styling and clean professional design.
 */
export function RouteRoyalPageHeader({
  title,
  subtitle,
  excerpt,
  featuredImage,
  showBreadcrumb = false,
  breadcrumbItems = [],
  className = '',
}: PageHeaderProps) {
  return (
    <header className={`relative ${className}`}>
      {/* Breadcrumb Navigation */}
      {showBreadcrumb && breadcrumbItems.length > 0 && (
        <nav aria-label="Breadcrumb" className="mb-6">
          <ol className="flex items-center gap-2 text-sm text-gray-500">
            {breadcrumbItems.map((item, index) => (
              <li key={index} className="flex items-center gap-2">
                {index > 0 && (
                  <svg
                    className="h-4 w-4 text-blue-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                )}
                {item.href ? (
                  <Link
                    href={item.href}
                    className="text-gray-600 hover:text-blue-600 transition-colors font-medium"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <span className="text-gray-400">{item.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}

      {/* Featured Image */}
      {featuredImage && (
        <div className="relative w-full aspect-[21/9] mb-8 rounded-xl overflow-hidden shadow-lg">
          <Image
            src={featuredImage.url}
            alt={featuredImage.alt || title}
            fill
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 via-transparent to-transparent" />
          {featuredImage.caption && (
            <div className="absolute bottom-0 left-0 right-0 text-white text-sm p-4">
              {featuredImage.caption}
            </div>
          )}
        </div>
      )}

      {/* Title */}
      <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3 leading-tight text-gray-900">
        {title}
      </h1>

      {/* Subtitle */}
      {subtitle && (
        <p className="text-base text-blue-600 font-medium mb-3">
          {subtitle}
        </p>
      )}

      {/* Excerpt */}
      {excerpt && (
        <p className="text-lg text-gray-600 leading-relaxed">
          {excerpt}
        </p>
      )}
    </header>
  );
}
